import React, { useMemo } from 'react';

import { useScopedPersistedSelection } from '@src/corelib/hooks';

const ActiveOrgContext = React.createContext(null);

const STORAGE_PREFIX = 'ao-active-org:';


/** Resolve the active membership from a stored id, falling back to the first
 * membership when the stored one is gone (org left/deleted) or absent. */
function resolveMembership(memberships, storedId) {
  const list = memberships || [];
  if (storedId) {
    const match = list.find((entry) => String(entry.id) === String(storedId));
    if (match) {
      return match;
    }
  }
  return list[0] || null;
}


/** Owns the selected membership across the app (#213). The selection is the
 * membership id, persisted in localStorage keyed by USER id so two accounts on
 * one browser never inherit each other's org. Switching orgs changes every
 * seller-scoped query key, so React Query refetches naturally; consumers that
 * keep transient per-org UI state (Listings selection) re-key on the seller id.
 *
 * `memberships` and `userId` are supplied by the /app shell (it already runs the
 * mine + me queries), so this provider does no fetching of its own. */
function ActiveOrgProvider({ memberships, userId, children }) {
  // Selected membership id, persisted per user (re-reads when the user changes).
  const [selectedId, setActiveMembership] = useScopedPersistedSelection(STORAGE_PREFIX, userId, null);

  const membership = resolveMembership(memberships, selectedId);

  const contextValue = useMemo(() => ({
    membership,
    seller: membership?.seller || null,
    memberships: memberships || [],
    setActiveMembership,
  }), [membership, memberships, setActiveMembership]);

  return <ActiveOrgContext.Provider value={contextValue}>{children}</ActiveOrgContext.Provider>;
}

ActiveOrgProvider.displayName = 'ActiveOrgProvider';
export { ActiveOrgContext, ActiveOrgProvider };
